import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { reorderQuestions } from '../../services/questionService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import './AdminPages.css';

// Sortable Question Item Component
const SortableQuestionItem = ({ question }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="admin-card"
    >
      <div className="admin-card-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ fontSize: '1.5rem', cursor: 'grab' }}>⋮⋮</div>
          <div style={{ flex: 1 }}>
            <div className="admin-card-header">
              <div>
                <h3>{question.key}</h3>
                <span style={{ fontSize: '0.85rem', color: '#888' }}>
                  {question.pillar} • {question.type}
                </span>
              </div>
              <span className={`status-badge ${question.active ? 'active' : 'inactive'}`}>
                {question.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
              {question.text.en}
            </p>
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#888' }}>
              Order: {question.orderIndex} • Weight: {question.weight} • Max Score: {question.maxScore}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminQuestionsReorder = ({ questions: initialQuestions, onBack, onSave }) => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([...initialQuestions].sort((a, b) => a.orderIndex - b.orderIndex));
  const [saving, setSaving] = useState(false);
  const [groupByPillar, setGroupByPillar] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setQuestions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update orderIndex for all items
        return newItems.map((item, index) => ({
          ...item,
          orderIndex: index + 1
        }));
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare reorder data
      const reorderData = questions.map((q, index) => ({
        id: q.id,
        orderIndex: index + 1
      }));

      await reorderQuestions(reorderData, user?.uid);
      
      alert('Questions reordered successfully!');
      onSave();
    } catch (error) {
      console.error('Error reordering questions:', error);
      alert('Error reordering questions: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setQuestions([...initialQuestions].sort((a, b) => a.orderIndex - b.orderIndex));
  };

  const getQuestionsByPillar = () => {
    const grouped = {
      LEAD: [],
      EVOLVE: [],
      ALIGN: [],
      PERFORM: []
    };

    questions.forEach(q => {
      if (grouped[q.pillar]) {
        grouped[q.pillar].push(q);
      }
    });

    return grouped;
  };

  const renderGroupedView = () => {
    const grouped = getQuestionsByPillar();

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {Object.entries(grouped).map(([pillar, pillarQuestions]) => (
          pillarQuestions.length > 0 && (
            <div key={pillar}>
              <h3 style={{ marginBottom: '1rem', color: '#4caf50' }}>{pillar}</h3>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={pillarQuestions.map(q => q.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {pillarQuestions.map((question) => (
                      <SortableQuestionItem key={question.id} question={question} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )
        ))}
      </div>
    );
  };

  const renderFlatView = () => {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={questions.map(q => q.id)}
          strategy={verticalListSortingStrategy}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {questions.map((question) => (
              <SortableQuestionItem key={question.id} question={question} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Reorder Questions</h1>
          <p>Drag and drop to change question order</p>
        </div>
        <Button variant="secondary" onClick={onBack}>
          ← Back to Questions
        </Button>
      </div>

      <div className="admin-form-card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={groupByPillar}
                onChange={(e) => setGroupByPillar(e.target.checked)}
              />
              Group by Pillar
            </label>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button variant="secondary" onClick={handleReset} disabled={saving}>
              Reset
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Order'}
            </Button>
          </div>
        </div>
      </div>

      <div className="admin-page-content">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {groupByPillar ? renderGroupedView() : renderFlatView()}
        </motion.div>
      </div>

      <div style={{ 
        position: 'fixed', 
        bottom: '2rem', 
        right: '2rem', 
        background: '#1a1a1a', 
        padding: '1rem 2rem', 
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        display: 'flex',
        gap: '1rem'
      }}>
        <Button variant="secondary" onClick={onBack} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AdminQuestionsReorder;
